"use strict";

import * as Sentry from "@sentry/react";
import { onAuthStateChanged, signOut } from "firebase/auth";

import { auth } from "../../config/firebase.js";
import {
    clearStoredProjectFile,
    clearStoredUserInfo,
    getStoredUserInfo,
} from "../../utils/storage";

export const LOGIN_PATH = "/";
export const SESSION_EXPIRED_MESSAGE = "Your session has expired. Please sign in again.";

// Survives the redirect so the login page can explain why the user landed there.
const SESSION_EXPIRED_FLAG_KEY = "sessionExpired";

// Firebase errors that mean the session can't be refreshed and the user must sign in again.
const SESSION_ENDING_AUTH_ERROR_CODES = new Set([
    "auth/user-token-expired",
    "auth/user-disabled",
    "auth/user-not-found",
    "auth/invalid-user-token",
    "auth/requires-recent-login",
]);

let isRedirecting = false;

export const isSessionEndingAuthError = (error) => SESSION_ENDING_AUTH_ERROR_CODES.has(error?.code);

/**
 * Clears every trace of the current session and sends the user to the login page.
 * Safe to call repeatedly; concurrent failures only trigger one redirect.
 */
export const handleSessionExpired = (reason = "unknown") => {
    if (isRedirecting || window.location.pathname === LOGIN_PATH) {
        return;
    }
    isRedirecting = true;
    console.warn(`Session expired (${reason}); redirecting to login.`);
    Sentry.captureMessage("Session expired; redirecting to login", {
        level: "warning",
        tags: { sessionExpiredReason: reason },
    });

    clearStoredUserInfo();
    clearStoredProjectFile();
    try {
        sessionStorage.setItem(SESSION_EXPIRED_FLAG_KEY, reason);
    } catch (error) {
        console.error("Failed to record session expiry:", error);
    }
    if (auth.currentUser) {
        Promise.resolve()
            .then(() => signOut(auth))
            .catch((error) => console.error("Firebase sign out failed:", error));
    }
    window.location.replace(LOGIN_PATH);
};

/**
 * Returns the recorded expiry reason once after a session-expiry redirect, then clears it.
 * Returns null when the user did not land here because of an expiry.
 */
export const consumeSessionExpiredReason = () => {
    try {
        const reason = sessionStorage.getItem(SESSION_EXPIRED_FLAG_KEY);
        sessionStorage.removeItem(SESSION_EXPIRED_FLAG_KEY);
        return reason;
    } catch (error) {
        return null;
    }
};

/**
 * Redirects to login whenever Firebase reports no user while the app still holds a session,
 * e.g. the refresh token was revoked or expired in another tab.
 */
export const watchFirebaseSession = () => onAuthStateChanged(auth, (user) => {
    if (!user && getStoredUserInfo()) {
        handleSessionExpired("firebase-auth-state-null");
    }
});
